import { parseISO } from "date-fns";
import { useMemo } from "react";

import type { FeelingAtHomeDataMonthlyDto } from "@/api/types";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { ChartTooltip } from "@/components/Charts/ChartTooltip";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Select } from "@/design-system/Select";
import type { DateRange } from "@/queries/analytics/queryOptions";
import { Trans, useTranslation } from "@/translations";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "@/ui-adapters/Recharts";

type FahWidgetTrendChartProps = {
  data: FeelingAtHomeDataMonthlyDto[];
  selectedPeriod: DateRange;
  onChangePeriod: (period: DateRange) => void;
};

const MAX_VISIBLE_Y = 10;
const MAX_Y = MAX_VISIBLE_Y + 2;
const MIN_Y = 0;
const MARGIN_Y = 1;

export const FahWidgetTrendChart = ({
  data,
  selectedPeriod,
  onChangePeriod,
}: FahWidgetTrendChartProps): React.ReactNode => {
  const { t, i18n } = useTranslation();

  const chartData = useMemo(
    () =>
      data.map((stat) => ({
        x: parseChartDate({
          month: parseISO(stat.month).getMonth() + 1,
          year: parseISO(stat.month).getFullYear(),
        }).toISOString(),
        primary: stat.totalFeelingAtHomeScore?.rating ?? null,
        benchmark: stat.benchmarkFeelingAtHomeScore?.rating ?? null,
      })),
    [data],
  );

  const items: {
    label: string;
    value: DateRange;
  }[] = [
    {
      label: t("component.analytics-widget.fah.graph.option.6-months"),
      value: "last6Months",
    },

    {
      label: t("component.analytics-widget.fah.graph.option.12-months"),
      value: "lastYear",
    },
  ];

  const allYValues = useMemo(
    () => chartData.flatMap((d) => [d.primary, d.benchmark]).filter((y): y is number => y !== null),
    [chartData],
  );
  const minYDomain = allYValues.length > 0 ? Math.floor(Math.min(...allYValues)) : MIN_Y;
  const maxYDomain = allYValues.length > 0 ? Math.ceil(Math.max(...allYValues)) : MAX_VISIBLE_Y;
  const minYAxis = Math.max(minYDomain - MARGIN_Y, MIN_Y);
  const maxYAxis = Math.min(maxYDomain + MARGIN_Y, MAX_Y);
  const yAxisTicks = useMemo(
    () => Array.from({ length: maxYAxis - minYAxis + 1 }, (_, index) => minYAxis + index),
    [maxYAxis, minYAxis],
  );

  return (
    <section className="flex w-full flex-col items-center justify-self-center pt-space-md @xl:pt-space-lg">
      <h2 className="flex items-center gap-space-sm text-center text-heading-3">
        <Trans
          i18nKey="component.analytics-widget.fah.graph.title"
          components={{
            select: (
              <span className="w-search-compact text-body">
                <Select
                  items={items}
                  keySelector={(item) => item.value}
                  renderOption={(item) => item.label}
                  selected={items.find((item) => item.value === selectedPeriod)}
                  onChange={(item) => {
                    onChangePeriod(item.value);
                  }}
                />
              </span>
            ),
          }}
        />
      </h2>
      <section className="flex w-full flex-col items-center gap-space-sm p-space-md @xl:p-space-lg">
        <figure className="aspect-18/10 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
              <defs>
                <linearGradient id="area-background" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0C275A" stopOpacity={0.24} />
                  <stop offset="100%" stopColor="#0C275A" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={dataColors.axis} strokeDasharray="2 2" horizontal={false} strokeWidth={0.5} />
              <CartesianGrid stroke={dataColors.axis} vertical={false} strokeWidth={0.5} />
              <XAxis
                dataKey="x"
                tickFormatter={(value) => formatDate(i18n, "monthShort", value)}
                tickLine={false}
                axisLine={{ stroke: dataColors.axis, strokeWidth: 0.5 }}
                tick={{ fontSize: 10 }}
                dy={10}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[minYAxis, maxYAxis]}
                ticks={yAxisTicks}
                tickLine={false}
                axisLine={false}
                tickFormatter={(tick) => (tick > MAX_VISIBLE_Y ? "" : Math.round(tick).toFixed(1))}
                tick={{ fontSize: 10 }}
                width={24}
              />
              <Tooltip cursor={{ stroke: dataColors.axis }} content={<CustomTooltip />} />
              <Line
                type="linear"
                dataKey="benchmark"
                stroke={dataColors.benchmark}
                strokeWidth={4}
                dot={false}
                activeDot={false}
                connectNulls={false}
              />
              <Area
                type="linear"
                dataKey="primary"
                stroke={dataColors.primary}
                fill="url(#area-background)"
                strokeWidth={4}
                connectNulls={false}
                dot={<CustomizedDot />}
                activeDot={<CustomizedActiveDot />}
              />
            </AreaChart>
          </ResponsiveContainer>
        </figure>

        <ChartLegend
          items={[
            {
              label: t("component.analytics-widget.fah.graph.legend.primary"),
              color: dataColors.primary,
            },
            {
              label: t("common.label.aop-benchmark"),
              color: dataColors.benchmark,
            },
          ]}
        />
      </section>
    </section>
  );
};

function CustomizedDot(props: any) {
  const { cx, cy, payload } = props;
  if (payload.primary === null) {
    return null;
  }

  return <circle cx={cx} cy={cy} r={4} stroke={dataColors.primary} strokeWidth={1} fill="#fff" />;
}

function CustomizedActiveDot(props: any) {
  const { cx, cy, payload } = props;
  if (payload.primary === null) {
    return null;
  }

  return (
    <circle cx={cx} cy={cy} r={6} fill="transparent" strokeWidth={4} stroke={dataColors.primary} strokeOpacity={0.3} />
  );
}

function CustomTooltip({
  active,
  payload,
  label,
}: Partial<{ active: boolean; payload: { dataKey: string; value: number }[]; label: string }>) {
  const { t, i18n } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  const primaryPayload = payload.find((p) => p.dataKey === "primary");
  if (primaryPayload?.value == null || !label) {
    return null;
  }

  return (
    <ChartTooltip>
      <span className="text-caption-emphasis">
        {t("component.analytics-widget.fah.graph.tooltip", {
          score: primaryPayload.value.toFixed(1),
        })}
      </span>
      <br />
      <span className="text-caption">{formatDate(i18n, "monthYearShort", label)}</span>
    </ChartTooltip>
  );
}
